import React from "react";
import Link from "next/link";
import Image from "next/image";
import { AspectRatio } from "@/v2-components/ui/aspect-ratio";
import { useTrans } from "@/utils/useTrans";
import { cn } from "@/libs/utils";
import { buttonVariants } from "@/v2-components/ui/button";
import Container from "@/components/commons/Container";
import { DeviceType } from "@/services/ContentsService";
import { BhapticsContent } from "@/store/reducers/contents";
import { useGames } from "@/api/game-api";
import { Skeleton } from "@/v2-components/ui/skeleton";
import { SUPPORTED_GAMES_HREF } from "@/v2-components/pages/games/GamesSubNav";

type Props = {
  className?: string;
  deviceType: keyof typeof DeviceType | "";
};

interface Option {
  name: string;
  value: string;
}

const options = [
  { name: "All", value: "" },
  { name: "Steam VR", value: "steam" },
  { name: "Meta Quest", value: "quest" },
  { name: "PSVR2", value: "psvr2" },
  { name: "PC Game", value: "pcgames" },
];

const filterOptions = (
  options: Option[],
  items: BhapticsContent[],
): Option[] => {
  return options.filter((option) => {
    if (option.value === "") return true; // Always keep the "All" option
    return items.some((item) =>
      item.supportedPlatforms.map((p) => p.platform).includes(option.value),
    );
  });
};

const BhapticsContentCards = ({ className, deviceType }: Props) => {
  const { t } = useTrans(["tactsuit"]);
  const [filter, setFilter] = React.useState("");

  // 클라이언트에서 직접 데이터 로드
  const { data: contentItems = [], isLoading } = useGames({
    filters: deviceType ? [deviceType] : [],
    sortBy: "popularity",
  });

  const filteredOptions = filterOptions(options, contentItems);
  const filteredContents = contentItems.filter((p) => {
    if (!filter) return true;
    return p.supportedPlatforms
      .filter((p) => p.enabled)
      .map((p) => p.platform)
      .includes(filter);
  });
  const contents = filteredContents.slice(0, 8);

  return (
    <Container className={cn("mt-16 max-w-screen-mdl laptop:mt-24", className)}>
      <div
        className={
          "flex flex-col items-center justify-between laptop:flex-row laptop:py-6"
        }
      >
        <div className="flex w-full flex-1 flex-col gap-3 text-center laptop:gap-5 laptop:text-start">
          <h2 className="mx-4 whitespace-pre-line text-3xl laptop:mx-0 laptop:whitespace-normal laptop:text-5xl">
            {t(`Upgrade Your Content Experience`)}
          </h2>
          {isLoading ? (
            <Skeleton className="mx-auto h-5 w-64 laptop:mx-0" />
          ) : (
            <p className="whitespace-pre-line text-base laptop:whitespace-normal laptop:text-lg">
              {t(
                `Dive into a collection of over NNN compatible games and uncover your next obsession.`,
              ).replace("NNN", `${filteredContents.length}`)}
            </p>
          )}
        </div>

        <div className="mt-10 flex gap-5 laptop:mt-0">
          {filteredOptions.map((platform, index) => (
            <button
              key={index}
              onClick={() => {
                setFilter(platform.value);
              }}
              className={cn(
                `text-base`,
                filter === platform.value ? "font-600" : "",
              )}
            >
              {platform.name}
            </button>
          ))}
        </div>
      </div>

      {/* Desktop */}
      <div className="mt-9 grid grid-cols-2 gap-x-4 gap-y-5 laptop:grid-cols-4 laptop:gap-x-6 laptop:gap-y-10">
        {isLoading
          ? Array.from({ length: 8 }).map((_, index) => (
              <div
                key={index}
                data-index={index}
                className="flex-col gap-4.5 data-[index=6]:hidden data-[index=7]:hidden data-[index=6]:laptop:flex data-[index=7]:laptop:flex"
              >
                <div className="flex w-full overflow-hidden rounded-[10px] laptop:rounded-2xl">
                  <AspectRatio ratio={281 / 131}>
                    <Skeleton className="h-full w-full rounded-none" />
                  </AspectRatio>
                </div>
                <Skeleton className="mt-2 h-5 w-3/4" />
              </div>
            ))
          : contents.map((item, index) => (
              <Link
                key={item.id}
                data-index={index}
                className="flex-col gap-4.5 data-[index=6]:hidden data-[index=7]:hidden data-[index=6]:laptop:flex data-[index=7]:laptop:flex"
                href={`/games/experiences/${item.slug}`}
              >
                <div className="flex w-full overflow-hidden rounded-[10px] bg-coolgray-10 laptop:rounded-2xl">
                  <AspectRatio ratio={281 / 131}>
                    <Image
                      src={item.thumbnailWideLink}
                      placeholder="blur"
                      blurDataURL="/placeholder.png"
                      alt={""}
                      className={"object-cover"}
                      fill
                    />
                  </AspectRatio>
                </div>
                <p className="text-lg">{item.name}</p>
              </Link>
            ))}
      </div>

      <div className="mt-10 flex items-center justify-center">
        <Link
          /* ★개편 이전 화면으로 가고 있었다 (#5648 후속). 이 카드는 **Visor 전용**이라
             (`ProductFeatureSections.tsx` 의 `slug === TACTVISOR` 분기) VR 목록으로
             보낸다 — 「일반 링크는 PC」 선례의 예외다. 작성자 결정(2026-09-09).

             ★**`device` 축을 함께 나른다** (R1 M3). 카드 3장은 `deviceType`(Visor면
             `"Head"`)으로 좁혀진 목록인데 초판의 `See All` 은 그 축을 버려서, 카드를
             보다 누르면 **Visor 무관 게임까지** 나왔다. VR 목록에 그 축이 있다
             (`use-vr-games-filters.ts` `GROUP_DEFS` 의 `device`). 실측(2026-09-09 dev ·
             고유 slug): `/games/vr/games/` 66 → `?device=Head` **46**.

             값은 `deviceType` 을 그대로 넘긴다 — 손으로 `Head` 를 적으면
             `ContentsService.slugToFilters` 와 갈라진다. 그 함수는 매칭 안 되는 slug 에
             **빈 문자열**을 주므로 그때는 맨 목록으로 폴백한다. */
          href={
            deviceType
              ? `${SUPPORTED_GAMES_HREF.vr}/?device=${deviceType}`
              : SUPPORTED_GAMES_HREF.vr
          }
          className={buttonVariants({
            variant: "darkgray-fill",
            size: "sm",
            shape: "pill",
            className: "px-6",
          })}
        >
          {t("Learn More")}
        </Link>
      </div>
    </Container>
  );
};

export default BhapticsContentCards;
